import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, X, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import HorizonSelector from "@/components/HorizonSelector";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";

function shortMonth(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  } catch { return iso; }
}
const num = (s) => {
  if (s === "" || s == null) return 0;
  const n = parseFloat(String(s).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export default function Actuals() {
  const [horizon, setHorizon] = useState(18);
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get(`/collections/summary?horizon=${horizon}`),
        api.get(`/collections/ledger?horizon=${horizon}`),
      ]);
      setSummary(s.data);
      setLedger(l.data);
      setEdits({});
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  }, [horizon]);
  useEffect(() => { load(); }, [load]);

  // Aggregate ledger into project × month grid with projected + actual
  const grid = useMemo(() => {
    if (!summary) return null;
    const months = summary.months;
    const byPM = {}; // project_name -> month -> { projected, actual, ids: [ledger row ids for that cell] }
    for (const r of ledger) {
      const pn = r.project_name;
      const m = r.month;
      if (!byPM[pn]) byPM[pn] = {};
      if (!byPM[pn][m]) byPM[pn][m] = { projected: 0, actual: 0, ids: [] };
      byPM[pn][m].projected += r.projected_amount || 0;
      byPM[pn][m].actual += r.actual_amount || 0;
      byPM[pn][m].ids.push(r.id);
    }
    const rows = summary.projects.map((p) => ({
      project_id: p.project_id,
      project_name: p.project_name,
      project_type: p.project_type,
      cells: months.map((m) => ({ month: m, ...(byPM[p.project_name]?.[m] || { projected: 0, actual: 0, ids: [] }) })),
    }));
    return { months, rows };
  }, [summary, ledger]);

  const setCell = (pn, m, v) => {
    const key = `${pn}::${m}`;
    setEdits((prev) => ({ ...prev, [key]: v }));
  };
  const cellVal = (pn, m, baseline) => {
    const key = `${pn}::${m}`;
    return key in edits ? edits[key] : (baseline || 0);
  };

  const save = async () => {
    if (!Object.keys(edits).length) return;
    setBusy(true);
    try {
      // Convert edits to bulk actuals rows
      const rows = Object.entries(edits).map(([k, v]) => {
        const [project_name, month] = k.split("::");
        return { project_name, month, amount: num(v) };
      });
      const res = await api.post("/actuals/bulk", { rows });
      toast.success(`Saved ${res.data.updated ?? rows.length} cells`);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const dirty = Object.keys(edits).length;
  if (!grid) return <PageShell title="Actuals vs Projections" kicker="Reconciliation"><div className="cf-card p-8 text-center">Loading…</div></PageShell>;

  const grandProj = grid.rows.reduce((s, r) => s + r.cells.reduce((a, c) => a + c.projected, 0), 0);
  const grandAct = grid.rows.reduce((s, r) => s + r.cells.reduce((a, c) => a + (num(cellVal(r.project_name, c.month, c.actual))), 0), 0);
  const grandVar = grandAct - grandProj;

  return (
    <PageShell
      testId="actuals-page"
      kicker="Reconciliation"
      title="Actuals vs Projections"
      hideProjectFilter
      rangeMonths={summary?.months}
      rangeLabel={`${horizon}m`}
      actions={<HorizonSelector value={horizon} onChange={setHorizon} months={summary?.months} />}
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-2xl">
        Enter the actual monthly collections received per project. Values save in bulk and immediately update the variance vs projection.
      </p>

      <div className="grid grid-cols-3 gap-2 md:gap-3">
        <div className="glass-card p-3 md:p-4 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold truncate">Projected · {horizon}m</div>
          <div className="num text-sm md:text-lg text-ink font-bold mt-0.5 truncate">{formatMoney(grandProj)}</div>
        </div>
        <div className="glass-card p-3 md:p-4 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold truncate">Actual</div>
          <div className="num text-sm md:text-lg text-ink font-bold mt-0.5 truncate">{formatMoney(grandAct, { showZero: true })}</div>
        </div>
        <div className="glass-card p-3 md:p-4 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold truncate">Variance</div>
          <div className={`num text-sm md:text-lg font-bold mt-0.5 flex items-center gap-1 truncate ${grandVar > 1 ? "text-emerald-600" : grandVar < -1 ? "text-red-600" : "text-ink"}`}>
            {grandVar > 1 ? <TrendingUp size={12} className="shrink-0" /> : grandVar < -1 ? <TrendingDown size={12} className="shrink-0" /> : <Minus size={12} className="shrink-0" />}
            <span className="truncate">{formatMoney(grandVar, { showZero: true })}</span>
          </div>
        </div>
      </div>

      <div className="glass-card overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="sticky left-0 z-20 bg-ink text-white text-[10px] uppercase tracking-widest font-semibold px-4 py-3 text-left" style={{ minWidth: 200 }}>Project</th>
              {grid.months.map((m) => (
                <th key={m} className="bg-ink text-white text-[10px] uppercase tracking-widest px-2 py-3 text-right" style={{ minWidth: 100 }}>{shortMonth(m)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((r, i) => (
              <tr key={r.project_id} className={i % 2 === 0 ? "bg-surface/70" : "bg-surface/50"}>
                <td className="sticky left-0 z-10 px-4 py-1.5 text-sm text-ink font-medium" style={{ background: i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)", borderRight: "1px solid var(--cf-hairline)" }}>
                  {r.project_name}
                  <span className="ml-2 text-[9px] uppercase text-muted-light">{r.project_type}</span>
                </td>
                {r.cells.map((c) => {
                  const key = `${r.project_name}::${c.month}`;
                  const dirtyCell = key in edits;
                  const val = dirtyCell ? edits[key] : (c.actual || "");
                  return (
                    <td key={c.month} className={`px-1 py-1 align-middle ${dirtyCell ? "bg-amber-100/60" : ""}`} style={{ minWidth: 100 }}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={val === 0 ? "" : val}
                        onChange={(e) => setCell(r.project_name, c.month, e.target.value)}
                        placeholder={c.projected > 0 ? formatMoney(c.projected, { compact: true }) : "0"}
                        title={`Projected: ${formatMoney(c.projected)}`}
                        className="num w-full text-right text-xs px-2 py-1.5 bg-transparent rounded focus:outline-none focus:bg-surface focus:ring-2 focus:ring-indigo-300 text-ink placeholder:text-muted-light/50"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dirty > 0 && (
        <div className="fixed bottom-4 right-4 z-40">
          <div className="glass-card flex items-center gap-3 p-3 shadow-xl border border-amber-300/70">
            <div className="text-sm text-ink">
              <span className="font-semibold">{dirty}</span>{" "}
              <span className="text-muted-dark">unsaved</span>
            </div>
            <button onClick={() => setEdits({})} disabled={busy} className="cf-touch inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-surface text-muted-dark text-sm">
              <X size={13} /> Discard
            </button>
            <button onClick={save} disabled={busy} className="cf-touch inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold disabled:opacity-60">
              <Save size={13} className="text-amber-300" /> {busy ? "Saving…" : `Save ${dirty}`}
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
}
